package com.siriusxm.cmc.smithy

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import com.siriusxm.pia.components.ExpandingTableController
import com.siriusxm.pia.components.expandingRowTable
import com.siriusxm.pia.components.pill
import com.siriusxm.smithy4kt.*
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

object EntityModelStyles : StyleSheet() {
    val entityMemberName by style {
        fontWeight(700)
        display(DisplayStyle.InlineBlock)
        marginRight(1.em)
    }

    val entityMemberType by style {
        fontSize(.8.em)
    }

    val shapeDocumentation by style {
        fontSize(.8.em)
    }

    val nestedShape by style {
        paddingTop(5.px)
    }

    val shapeConstraint by style {
        fontSize(.8.em)
        fontWeight(700)
    }

    val serviceDocs by style {
        margin(1.em, 0.px)
    }

    val serviceHttp by style {
        marginTop(5.px)
    }

    val serviceOperationDocs by style {
        marginTop(5.px)
    }

    val serviceOperationDetails by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(1.em)
    }

    val serviceOperationSection by style {
        type("h3").style {
            margin(0.px, 0.px, 3.px, 0.px)
        }
    }

    val serviceMethod by style {
        fontWeight(700)
        padding(3.px, 5.px)
        display(DisplayStyle.InlineBlock)
        marginRight(1.em)
        borderRadius(3.px)
        color(Color.white)

        backgroundColor(Color("#999"))
        (self + className("POST")).style {
            backgroundColor(Color("#49cc90"))
        }
        (self + className("GET")).style {
            backgroundColor(Color("#61affe"))
        }
        (self + className("DELETE")).style {
            backgroundColor(Color("#f93e3e"))
        }
        (self + className("PUT")).style {
            backgroundColor(Color("#fca130"))
        }
    }
    val serviceUri by style {
        fontFamily("monospace")
    }
}

/**
 * Renders a Smithy shape documentation view. When a shape is rendered as part of a member,
 * [additionalTraits] carries the member-level traits so that constraints like @pattern
 * applied directly on a member (rather than the target type) are still displayed.
 */
@Composable
fun Shape(shape: SmithyShape, controller: ExpandingTableController? = null, additionalTraits: Traits? = null) {
    when (shape) {
        is SmithyStructure -> {
            Structure(shape, controller = controller)
        }

        is SmithyMap -> {
            SmithyMapView(shape, controller)
        }

        is SmithyList -> {
            SmithyListView(shape, controller)
        }

        is SmithyEnum -> {
            EnumShape(shape)
        }

        is SmithyUnion -> {
            UnionShape(shape, controller)
        }

        is SmithyString -> SmithyStringView(shape, additionalTraits)
        is SmithyNumber<*> -> SmithyNumberView(shape, additionalTraits)
        is SmithyTimestamp -> SmithyTimestampView(shape)
        else -> {

        }
    }
}

/**
 * Helper to determine whether the member has the trait.
 */
fun SmithyMember.hasTrait(trait: String): Boolean {
    return this.traits.get("smithy.api#$trait") != null
}

/**
 * Renders a Smithy Structure definition view.
 */
@Composable
fun Structure(
    structure: SmithyStructure,
    labelRenderer: (@Composable (SmithyMember) -> Unit)? = null,
    controller: ExpandingTableController? = null
) {
    expandingRowTable<SmithyMember>(controller = controller) {
        items = structure.allMembers.values.toList()

        alwaysShowCollapsed = true
        collapsedContent { member ->
            Span({ classes(EntityModelStyles.entityMemberName) }) {
                if (labelRenderer != null) {
                    labelRenderer(member)
                } else {
                    Text(member.name)
                }
            }
            Span({ classes(EntityModelStyles.entityMemberType) }) {
                val shapeName = when (val memberShape = member.shape) {
                    is SmithyList -> {
                        "list of ${memberShape.member.id.substringAfter("#")}"
                    }

                    is SmithyMap -> {
                        "map of ${memberShape.key.id.substringAfter("#")} to ${memberShape.value.id.substringAfter("#")}"
                    }

                    else -> {
                        memberShape.id.substringAfter("#")
                    }
                }
                Text(shapeName)
                if (member.required) {
                    Text("*")
                }
            }
        }
        expandedContent { member ->
            val docs = member.documentation ?: member.shape.documentation
            docs?.let {
                Div({ classes(EntityModelStyles.shapeDocumentation) }) {
                    Text(it)
                }
            }
            Div({ classes(EntityModelStyles.nestedShape) }) {
                key(member.name) {
                    Shape(member.shape, controller, member.traits)
                }
            }
        }
    }
}

/**
 * Renders a Smithy Union definition view showing all variant members in an expandable table.
 */
@Composable
internal fun UnionShape(union: SmithyUnion, controller: ExpandingTableController? = null) {
    val members = union.members
    expandingRowTable<String>(controller = controller) {
        items = members.keys.toList()

        alwaysShowCollapsed = true
        collapsedContent { memberName ->
            val member = members.getValue(memberName)
            Span({ classes(EntityModelStyles.entityMemberName) }) {
                Text(member.name)
            }
            Span({ classes(EntityModelStyles.entityMemberType) }) {
                val shapeName = when (val memberShape = member.shape) {
                    is SmithyList -> {
                        "list of ${memberShape.member.id.substringAfter("#")}"
                    }

                    is SmithyMap -> {
                        "map of ${memberShape.key.id.substringAfter("#")} to ${memberShape.value.id.substringAfter("#")}"
                    }

                    else -> {
                        memberShape.id.substringAfter("#")
                    }
                }
                Text(shapeName)
            }
        }
        expandedContent { memberName ->
            val member = members.getValue(memberName)
            val docs = member.documentation ?: member.shape.documentation
            docs?.let {
                Div({ classes(EntityModelStyles.shapeDocumentation) }) {
                    Text(it)
                }
            }
            Div({ classes(EntityModelStyles.nestedShape) }) {
                key(member.name) {
                    Shape(member.shape, controller, member.traits)
                }
            }
        }
    }
}

fun plural(count: Int?, base: String): String {
    return "${base}${if (count != 1) "s" else ""}"
}

/**
 * A smithy list documentation view.
 */
@Composable
fun SmithyListView(list: SmithyList, controller: ExpandingTableController? = null) {
    Div {
        list.length?.let {
            shapeConstraint(
                if (it.min != null && it.max != null) {
                    if (it.min == it.max) {
                        "Exactly ${it.min} ${plural(it.min, "item")}"
                    }
                    "Between ${it.min} and ${it.max} ${plural(it.min, "item")}"
                } else if (it.min != null) {
                    "At least ${it.min} ${plural(it.min, "item")}"
                } else if (it.max != null) {
                    "At most ${it.max} ${plural(it.min, "item")}"
                } else null
            )
        }

        Shape(list.member, controller)
    }
}

/**
 * A smithy map documentation view.
 */
@Composable
fun SmithyMapView(map: SmithyMap, controller: ExpandingTableController? = null) {
    Shape(map.value, controller)
}

/**
 * A Smithy String documentation view. Displays pattern and length constraints.
 * [additionalTraits] are checked as a fallback for constraints like @pattern that
 * may be applied at the member level rather than on the target type.
 */
@Composable
fun SmithyStringView(shape: SmithyString, additionalTraits: Traits? = null) {
    val pattern = shape.pattern
        ?: additionalTraits?.smithyTrait("pattern")?.jsonPrimitive?.contentOrNull?.let { Regex(it) }
    pattern?.let {
        shapeConstraint("Pattern: ${it.pattern}")
    }
    shape.length?.let {
        shapeConstraint(
            if (it.min != null && it.max != null) {
                if (it.min == it.max) {
                    "Exactly ${it.min} ${plural(it.min, "character")} long"
                } else {
                    "Between ${it.min} and ${it.max} characters long"
                }
            } else if (it.min != null) {
                "At least ${it.min} ${plural(it.min, "character")} long"
            } else if (it.max != null) {
                "At most ${it.max} ${plural(it.max, "character")} long"
            } else null
        )
    }
}

@Composable
fun shapeConstraint(message: String?) {
    if (message != null) {
        Div({ classes(EntityModelStyles.shapeConstraint) }) {
            Text(message)
        }
    }
}

/**
 * Displays documentation for a Smithy number definition. [additionalTraits] are checked
 * as a fallback for @range constraints applied at the member level.
 */
@Composable
fun SmithyNumberView(shape: SmithyNumber<*>, additionalTraits: Traits? = null) {
    val min = shape.min ?: additionalTraits?.smithyTrait("range")?.jsonObject?.get("min")?.jsonPrimitive?.contentOrNull
    val max = shape.max ?: additionalTraits?.smithyTrait("range")?.jsonObject?.get("max")?.jsonPrimitive?.contentOrNull
    shapeConstraint(
        if (min != null && max != null) {
            "Must be between $min and $max"
        } else if (min != null) {
            "Must greater than or equal to $min"
        } else if (max != null) {
            "Must be less than or equal to $max"
        } else {
            null
        }
    )
}

@Composable
fun SmithyTimestampView(shape: SmithyTimestamp) {
    val format = shape.traits?.get("smithy.api#timestampFormat")
        ?.jsonPrimitive
        ?.contentOrNull ?: "date-time"

    val isDefault = format == "date-time" &&
            !shape.traits?.containsKey("smithy.api#timestampFormat")!!

    val example = when (format) {
        "epoch-seconds" -> "1712764910.123"
        "http-date"     -> "Tue, 10 Apr 2025 16:35:00 GMT"
        else            -> "2025-04-10T16:35:00Z"
    }

    val message = buildString {
        append("Format: $format")
        if (isDefault) append(" (default)")
        append(" — e.g. $example")
    }

    shapeConstraint(message)
}

/**
 * Displays documentation for a Smithy enumeration.
 */
@Composable
fun EnumShape(shape: SmithyEnum) {
    Div({
        style {
            display(DisplayStyle.Flex)
            margin(3.px, 0.px)
            gap(6.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        shape.allEntries.forEach {
            pill(it.value)
        }
    }
}
